(() => {
  const config = document.querySelector('[data-ff-attribution]');
  if (!config) return;

  const storageKey = 'ff:discovery:v1';
  const lifetime = 30 * 60 * 1000;
  const sources = {
    chatgpt: ['ai_referral', ['chatgpt', 'chatgpt.com', 'chat.openai.com']],
    perplexity: ['ai_referral', ['perplexity', 'perplexity.ai']],
    claude: ['ai_referral', ['claude', 'claude.ai']],
    google: ['search_referral', ['google', 'google.com', 'google.co.za', 'google.co.uk']],
    bing: ['search_referral', ['bing', 'bing.com']],
    duckduckgo: ['search_referral', ['duckduckgo', 'duckduckgo.com']],
  };
  const landings = ['index', 'product', 'collection', 'gifting', 'refills', 'page', 'cart', 'search'];
  const attributeNames = ['__ff_source__', '__ff_channel__', '__ff_evidence__', '__ff_landing__'];
  let record = null;
  let cartCount = Number(config.dataset.cartCount) || 0;
  let writeQueue = Promise.resolve();
  let lastWritten = null;
  let landingPublished = false;

  function allowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed() === true && privacy?.marketingAllowed() === true;
    } catch {
      return false;
    }
  }

  function valid(candidate) {
    return candidate && Object.hasOwn(sources, candidate.source) &&
      candidate.channel === sources[candidate.source][0] &&
      ['utm_source', 'referrer'].includes(candidate.evidence) &&
      landings.includes(candidate.landing) && Number.isFinite(candidate.created) &&
      Date.now() >= candidate.created && Date.now() - candidate.created < lifetime;
  }

  function stored() {
    try {
      const candidate = JSON.parse(sessionStorage.getItem(storageKey));
      return valid(candidate) ? { ...payload(candidate), created: candidate.created } : null;
    } catch {
      return null;
    }
  }

  function save(candidate) {
    try {
      if (candidate) sessionStorage.setItem(storageKey, JSON.stringify(candidate));
      else sessionStorage.removeItem(storageKey);
    } catch {}
  }

  function arrival() {
    const url = new URL(window.location.href);
    const taggedSource = url.searchParams.get('utm_source')?.toLowerCase().trim();
    let host = '';
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin !== url.origin) host = referrer.hostname.toLowerCase();
    } catch {}
    for (const [source, [channel, aliases]] of Object.entries(sources)) {
      const tagged = aliases.includes(taggedSource);
      const referred = aliases.some((alias) => alias.includes('.') && (host === alias || host.endsWith(`.${alias}`)));
      if (tagged || (!taggedSource && referred)) {
        return {
          source, channel, evidence: tagged ? 'utm_source' : 'referrer',
          landing: landings.includes(config.dataset.landing) ? config.dataset.landing : 'page',
          created: Date.now(),
        };
      }
    }
    return host || taggedSource ? false : null;
  }

  function active() {
    return allowed() && valid(record) ? record : null;
  }

  function payload(candidate) {
    return {
      source: candidate.source,
      channel: candidate.channel,
      evidence: candidate.evidence,
      landing: candidate.landing,
    };
  }

  function publish(name, candidate = active()) {
    if (!candidate || !allowed()) return;
    try {
      const result = window.Shopify?.analytics?.publish(`ff:${name}`, payload(candidate));
      if (result?.catch) result.catch(() => {});
    } catch {}
  }

  function attributes(candidate) {
    const values = candidate ? Object.values(payload(candidate)) : ['', '', '', ''];
    return Object.fromEntries(attributeNames.map((name, index) => [name, values[index]]));
  }

  function syncCart() {
    writeQueue = writeQueue.catch(() => {}).then(async () => {
      const candidate = active();
      const data = attributes(candidate);
      const signature = JSON.stringify(data);
      if (lastWritten === signature) return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      try {
        const response = await fetch(config.dataset.cartUpdateUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: data }), signal: controller.signal,
        });
        if (response.ok) {
          lastWritten = signature;
          if (candidate && active()) publish('cart_attributed', candidate);
        }
      } catch {} finally {
        clearTimeout(timeout);
      }
    });
  }

  function removeFields(root = document) {
    root.querySelectorAll('[data-ff-attribution-field]').forEach((field) => field.remove());
  }

  function addField(form, name, value) {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = value;
    field.dataset.ffAttributionField = '';
    form.appendChild(field);
  }

  function contactField() {
    const form = document.getElementById('ContactForm');
    if (!form) return;
    removeFields(form);
    const candidate = active();
    if (candidate) {
      addField(form, 'contact[Discovery source]',
        `${candidate.source} / ${candidate.channel} / ${candidate.evidence} / ${candidate.landing}`);
    }
  }

  function consentChanged() {
    if (!allowed()) {
      record = null;
      save(null);
      removeFields();
      if (cartCount > 0 || lastWritten !== null) syncCart();
      return;
    }
    const incoming = arrival();
    record = incoming === false ? null : incoming || stored();
    save(record);
    contactField();
    if (incoming && !landingPublished) {
      publish('referral_landing');
      landingPublished = true;
    }
    if (cartCount > 0) syncCart();
  }

  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('.ff-enquiry-cta')) publish('enquiry_opened');
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.id === 'ContactForm') {
      contactField();
      publish('enquiry_submit_attempt');
      return;
    }
    if (form.tagName !== 'FORM') return;
    const action = new URL(form.action, window.location.href);
    const cart = new URL(config.dataset.cartUrl, window.location.href);
    if (action.origin !== cart.origin || action.pathname !== cart.pathname) return;
    removeFields(form);
    for (const [name, value] of Object.entries(attributes(active()))) {
      addField(form, `attributes[${name}]`, value);
    }
  }, true);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event.cartData?.item_count;
      if (Number.isFinite(count)) cartCount = count;
      else if (event.productVariantId || event.cartData?.items?.length) cartCount = Math.max(cartCount, 1);
      if (cartCount > 0 || lastWritten !== null) syncCart();
    });
  }

  if (window.Shopify?.customerPrivacy) consentChanged();
  else if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
      if (!error) consentChanged();
    });
  }
})();
